import React, { useState, useEffect } from 'react';
import { Theme, Grid, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import TextField from '@mui/material/TextField';
import { CurrencyObject } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	amount: number;
	updateProductPrice: (updatedAmount: number) => void;
	currency: CurrencyObject;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const PriceInput = (props: Props) => {
	const [error, setError] = useState('');
	const { t } = useTranslation();
	const { classes, amount, currency } = props;
	const invalidNegativePrice = t('discountDialog.invalidNegativePrice');

	useEffect(() => {
		// For existing negative price
		if (amount < 0) {
			setError(t('discountDialog.invalidNegativePrice'));
		}
	}, [amount, invalidNegativePrice, t]);

	const handlePriceChange = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const updatedAmount = parseFloat(event.target.value) * 100 || 0;
		const errorText = updatedAmount < 0 ? invalidNegativePrice : '';
		if (errorText !== error) {
			setError(errorText);
		}
		props.updateProductPrice(updatedAmount);
	};

	return (
		<Grid container spacing={0} className={classes.container}>
			<Grid item>
				<TextField
					defaultValue={amount || amount === 0 ? amount / 100 : ''}
					onBlur={handlePriceChange}
					variant="outlined"
					type="number"
					onClick={(e) => {
						e.stopPropagation();
					}}
					InputProps={{
						classes: {
							input: classes.input,
						},
					}}
					classes={{
						root: classes.inputField,
					}}
				/>
			</Grid>
			<Grid item className={classes.currencySymbol}>
				<Typography variant="body2">{currency.displayAs}</Typography>
			</Grid>
			<Grid item xs={12} className={classes.errorText}>
				<Typography variant="body2" color="error">
					{error}
				</Typography>
			</Grid>
		</Grid>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		inputField: {
			marginTop: '0px !important',
			width: '100%',
			maxWidth: '120px',
		},
		input: {
			padding: '4px 6px',
			fontSize: '1.6rem !important',
			lineHeight: 'normal',
			height: 32,
			textAlign: 'right',
		},
		container: {
			justifyContent: 'flex-end',
			alignItems: 'center',
		},
		currencySymbol: {
			paddingLeft: '5px',
		},
		errorText: {
			textAlign: 'right',
		},
	});

export default withStyles(styles)(PriceInput);
